'use client'

import { useEffect, useState } from 'react'

import { createPortal } from 'react-dom'

import { useTranslation } from '@/hooks/client/use-translation'

interface AccessoryModalProps {
  name: string
  // Server-rendered thumbnail (the Image component stays server-side); this
  // client shell only adds the open/close behavior.
  thumb: React.ReactNode
  // Server-rendered detail card: large image, name, tags, full about text.
  children: React.ReactNode
}

// Click the accessory thumbnail → overlay with the large image and full info,
// same portal-overlay pattern as ChangeModelModal.
export const AccessoryModal = ({ name, thumb, children }: AccessoryModalProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <>
      <button
        type="button"
        data-tracking="ignore"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={name}
        className="border-navy/10 hover:border-lapis flex h-14 w-14 shrink-0 cursor-zoom-in items-center justify-center rounded-lg border bg-white p-1 transition-colors"
      >
        {thumb}
      </button>

      {open &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={name}
            className="bg-overlay fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setOpen(false)}
          >
            <div
              className="relative max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {children}
              <button
                type="button"
                data-tracking="ignore"
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 cursor-pointer text-slate-400 transition-colors hover:text-slate-600"
                aria-label={t('aria.close', 'Close')}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}
